// Thùng rác tài liệu: khôi phục, xóa vĩnh viễn (kèm file trong IndexedDB), tự dọn sau 30 ngày
import { useState } from 'react';
import { Button, Input, Modal, Table, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { ArchiveRestore, Search, Trash2 } from 'lucide-react';
import DocShell from './components/DocShell';
import useDocsStore from './store';
import { TRASH_DAYS, type Doc } from './data';
import { deleteFiles } from './fileStore';
import { matches } from './logic';
import { StatePill, fmtDateTime, useDocAuth } from './utils';

export default function DocumentTrash() {
  const { can, me } = useDocAuth();
  const docs = useDocsStore(s => s.docs);
  const types = useDocsStore(s => s.types);
  const restoreDoc = useDocsStore(s => s.restoreDoc);
  const purgeDoc = useDocsStore(s => s.purgeDoc);
  const [modal, ctx] = Modal.useModal();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<React.Key[]>([]);

  const trash = docs.filter(d => d.deletedAt && matches(q, d.title, d.code)).sort((a, b) => b.deletedAt!.localeCompare(a.deletedAt!));
  const daysLeft = (d: Doc) => Math.max(0, TRASH_DAYS - dayjs().diff(dayjs(d.deletedAt), 'day'));
  const allowed = can('delete');

  const restore = (ids: string[]) => {
    ids.forEach(id => restoreDoc(id, me));
    setSelected([]);
    message.success(`Đã khôi phục ${ids.length} tài liệu`);
  };
  const purge = (ids: string[]) =>
    modal.confirm({
      title: `Xóa vĩnh viễn ${ids.length} tài liệu?`,
      content: 'Tài liệu và toàn bộ file đính kèm sẽ bị xóa hẳn, không thể khôi phục.',
      okText: 'Xóa vĩnh viễn', okButtonProps: { danger: true }, cancelText: 'Hủy',
      onOk: () => {
        const fileIds = ids.flatMap(id => purgeDoc(id));
        deleteFiles(fileIds);
        setSelected([]);
        message.success('Đã xóa vĩnh viễn');
      },
    });

  const columns: ColumnsType<Doc> = [
    { title: 'Mã tài liệu', dataIndex: 'code', width: 190, render: v => <span className="font-medium text-slate-600">{v}</span> },
    { title: 'Tên tài liệu', dataIndex: 'title', width: 300, render: (v, d) => <div><div className="font-medium text-slate-700">{v}</div><div className="text-[11px] text-slate-400">{types.find(t => t.id === d.typeId)?.name} · {d.files.length} file</div></div> },
    { title: 'Trạng thái trước khi xóa', key: 'st', width: 170, render: (_v, d) => <StatePill doc={d} /> },
    { title: 'Người xóa', dataIndex: 'deletedBy', width: 150 },
    { title: 'Thời gian xóa', dataIndex: 'deletedAt', width: 150, render: v => fmtDateTime(v) },
    {
      title: 'Tự xóa sau', key: 'left', width: 110, align: 'center',
      render: (_v, d) => <span className={daysLeft(d) <= 3 ? 'font-semibold text-red-500' : 'text-slate-500'}>{daysLeft(d)} ngày</span>,
    },
    ...(allowed ? [{
      title: 'Hành động', key: 'act', width: 110, align: 'center' as const,
      render: (_v: unknown, d: Doc) => (
        <div className="flex items-center justify-center gap-3">
          <Tooltip title="Khôi phục"><button onClick={() => restore([d.id])} className="text-slate-400 hover:text-[#357f96]"><ArchiveRestore size={16} /></button></Tooltip>
          <Tooltip title="Xóa vĩnh viễn"><button onClick={() => purge([d.id])} className="text-red-400 hover:text-red-600"><Trash2 size={16} /></button></Tooltip>
        </div>
      ),
    }] : []),
  ];

  return (
    <DocShell
      title="Thùng rác tài liệu"
      subtitle={`Tài liệu bị xóa được giữ ${TRASH_DAYS} ngày trước khi tự xóa vĩnh viễn`}
      actions={allowed && trash.length > 0 && <Button danger icon={<Trash2 size={15} />} onClick={() => purge(trash.map(d => d.id))}>Dọn sạch thùng rác</Button>}
    >
      {ctx}
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3">
          <Input allowClear prefix={<Search size={15} className="text-slate-400" />} placeholder="Tìm tên, mã tài liệu…" value={q} onChange={e => setQ(e.target.value)} className="max-w-sm" />
          {allowed && selected.length > 0 && (
            <>
              <span className="text-sm text-slate-500">Đã chọn {selected.length}</span>
              <Button icon={<ArchiveRestore size={14} />} onClick={() => restore(selected as string[])}>Khôi phục</Button>
              <Button danger icon={<Trash2 size={14} />} onClick={() => purge(selected as string[])}>Xóa vĩnh viễn</Button>
            </>
          )}
        </div>
        <Table<Doc>
          rowKey="id" columns={columns} dataSource={trash} scroll={{ x: 1250 }}
          rowSelection={allowed ? { selectedRowKeys: selected, onChange: setSelected } : undefined}
          locale={{ emptyText: 'Thùng rác trống' }}
          pagination={{ defaultPageSize: 20, showTotal: t => `${t} tài liệu` }}
        />
      </div>
    </DocShell>
  );
}
